import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDate, fmtMoney } from "../lib/format";
import { Plus, Download, CheckCircle2, Send, AlertTriangle, Clock } from "lucide-react";

const STATUS_TONE = {
  received: "pm-pill-green",
  pending: "pm-pill-amber",
  overdue: "pm-pill-red",
  due_soon: "pm-pill-amber",
  cancelled: "pm-pill-slate",
};

const PAYMENT_TYPE_LABEL = {
  monthly_commission: "Monthly commission",
  transfer_fee: "Transfer fee",
  bonus: "Bonus",
  solidarity: "FIFA solidarity",
  other: "Other",
};

function fmtAnnual(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `€${Math.round(n / 1000)}k`;
  return fmtMoney(n);
}

export default function CommissionsGlobal() {
  const [summary, setSummary] = useState(null);
  const [thisMonth, setThisMonth] = useState({ entries: [], total: 0 });
  const [streams, setStreams] = useState({ streams: [], total_annual: 0 });
  const [outstanding, setOutstanding] = useState([]);
  const [history, setHistory] = useState({ entries: [], total: 0 });
  const [historyLimit, setHistoryLimit] = useState(10);
  const [showLogModal, setShowLogModal] = useState(false);

  const monthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  const load = async () => {
    const [s, m, st, out, hist] = await Promise.all([
      api.get("/commissions/summary"),
      api.get(`/commissions/entries?month=${monthKey}`),
      api.get("/commissions/streams"),
      api.get("/commissions/outstanding"),
      api.get(`/commissions/history?limit=${historyLimit}&offset=0`),
    ]);
    setSummary(s.data);
    setThisMonth(m.data);
    setStreams(st.data);
    setOutstanding(out.data || []);
    setHistory(hist.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [historyLimit]);

  // Deep-link: when the dashboard urgent feed (or a notification) sends us
  // here with `?open=<entry.id>`, scroll that row into view and ring-
  // highlight it so the agent immediately sees which payment they came for.
  // The URL is then cleaned so a refresh doesn't re-trigger the animation.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});
  useEffect(() => {
    const target = searchParams.get("open");
    if (!target) return;
    // Wait for outstanding to load + row ref to attach before scrolling.
    if (outstanding.length === 0) return;
    const node = rowRefs.current[target];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(target);
      // Clean the URL once the animation has had a chance to play.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
      const t = setTimeout(() => setHighlightId(null), 3500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outstanding]);

  const markReceived = async (id) => {
    await api.patch(`/commissions/entries/${id}`, { status: "received" });
    load();
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/commissions/export-schedule.csv`;
    const token = localStorage.getItem("pm_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "payment-schedule.csv";
        a.click();
      });
  };

  return (
    <Layout title="Commissions" subtitle="Schedule · streams · outstanding payments">
      {summary?.hidden_by_permissions && (
        <div className="pm-card p-6 mb-6 bg-amber-50 border border-amber-200" data-testid="payroll-hidden-banner">
          <div className="flex items-start gap-3">
            <div className="text-amber-700 mt-0.5">🔒</div>
            <div>
              <div className="font-display font-bold text-base text-amber-900">Commission data is admin-only</div>
              <div className="text-sm text-amber-800 mt-1">
                Your workspace is configured so that only admins can see commission and payment figures. Ask your admin to change this in Settings → Permissions if you need access.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="commissions-stat-row">
        <StatCard label="Annual run-rate" value={summary?.annual_estimate ? fmtAnnual(summary.annual_estimate) : "—"} tone="green" testid="stat-annual" />
        <StatCard
          label={summary?.next_payment_window?.label ? `Next window · ${summary.next_payment_window.label}` : "Next payment window"}
          value={summary?.next_payment_window?.total ? fmtMoney(summary.next_payment_window.total) : "—"}
          sub={summary?.next_payment_window?.days_until != null ? `in ${summary.next_payment_window.days_until} days` : undefined}
          testid="stat-next-window"
        />
        <StatCard label="Outstanding" value={summary?.outstanding_total ? fmtMoney(summary.outstanding_total) : "—"} tone={summary?.outstanding_total > 0 ? "amber" : "default"} testid="stat-outstanding" />
        <StatCard label="Active streams" value={summary?.active_streams ?? "—"} testid="stat-streams" />
      </div>

      {/* Commission schedule — biannual representation windows */}
      <CommissionScheduleSection summary={summary} testid="schedule-section" />

      {/* Our share vs Owed to others (Phase 3) */}
      <BreakdownSection testid="breakdown-section" />

      {/* This month receipts */}
      <div className="pm-card p-6 mb-5" data-testid="receipts-this-month">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">Recent commission receipts</h3>
            <div className="text-xs text-slate-500 mt-0.5">{new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="pm-pill pm-pill-green font-mono-tab">{fmtMoney(thisMonth.total)}</span>
            <button onClick={() => setShowLogModal(true)} className="pm-btn-primary h-9 px-4 rounded-lg text-sm font-bold flex items-center gap-1.5" data-testid="log-payment-btn">
              <Plus size={14} /> Log payment received
            </button>
          </div>
        </div>
        {thisMonth.entries.length === 0 ? (
          <EmptyState text="No payments recorded for this month yet. Payments will appear here as you log commissions or when contracts generate automatic entries." />
        ) : (
          <div className="divide-y divide-slate-100">
            {thisMonth.entries.map((e) => <ReceiptRow key={e.id} entry={e} onMarkReceived={() => markReceived(e.id)} />)}
          </div>
        )}
      </div>

      {/* Annual commission overview */}
      <div className="pm-card p-6 mb-5" data-testid="streams-section">
        <h3 className="font-display text-xl font-bold tracking-tight mb-4">Annual commission overview</h3>
        {streams.streams.length === 0 ? (
          <EmptyState text="No active commission streams. Add active contracts with a wage and commission % to see streams here." />
        ) : (
          <>
            <div className="overflow-x-auto pm-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="py-2 font-bold">Player</th>
                    <th className="py-2 font-bold">Stream</th>
                    <th className="py-2 font-bold">Gross reference</th>
                    <th className="py-2 font-bold text-right">Annual commission</th>
                    <th className="py-2 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody className="font-mono-tab">
                  {streams.streams.map((s, i) => (
                    <tr key={`${s.contract_id}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`stream-row-${i}`}>
                      <td className="py-2.5 font-display font-bold text-slate-900">{s.client_name || "—"}</td>
                      <td className="py-2.5 text-slate-700">
                        {s.stream_type === "transfer_fee" ? "Transfer instalment" : "Monthly commission"} · {s.counterparty || "—"}
                      </td>
                      <td className="py-2.5 text-slate-600">
                        {s.commission_pct && s.wage_monthly
                          ? `${s.commission_pct}% of ${fmtMoney(s.wage_monthly, s.currency)}/mo`
                          : "—"}
                      </td>
                      <td className="py-2.5 text-right font-bold text-emerald-700">{fmtMoney(s.annual_commission, s.currency || "EUR")}</td>
                      <td className="py-2.5">
                        <span className={`pm-pill ${STATUS_TONE[s.status] || "pm-pill-green"}`}>{s.status || "active"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="py-3 text-right text-[11px] uppercase tracking-widest font-bold text-slate-500">Total annual commission estimate</td>
                    <td className="py-3 text-right font-display font-black text-lg font-mono-tab text-emerald-700">{fmtMoney(streams.total_annual)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Payment tracker — outstanding */}
      <div className="pm-card p-6 mb-5" data-testid="outstanding-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold tracking-tight">Outstanding payments</h3>
          <button onClick={exportCsv} className="pm-btn-secondary h-9 px-3 rounded-lg text-sm font-bold flex items-center gap-1.5" data-testid="export-schedule-btn">
            <Download size={14} /> Export schedule
          </button>
        </div>
        {outstanding.length === 0 ? (
          <EmptyState text="No outstanding payments. Anything due in the next 30 days will appear here." />
        ) : (
          <div className="divide-y divide-slate-100">
            {outstanding.map((e) => (
              <OutstandingRow
                key={e.id}
                entry={e}
                onMarkReceived={() => markReceived(e.id)}
                refCb={(node) => { if (node) rowRefs.current[e.id] = node; }}
                highlighted={highlightId === e.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* History */}
      <div className="pm-card p-6 mb-5" data-testid="history-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold tracking-tight">Payment history</h3>
          <button onClick={exportCsv} className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="export-history-btn">
            <Download size={12} /> Export CSV
          </button>
        </div>
        {history.entries.length === 0 ? (
          <EmptyState text="No payments received yet. As you mark payments as received, they'll appear here as a timeline." />
        ) : (
          <div className="space-y-3">
            {history.entries.map((e, i) => <HistoryRow key={e.id} entry={e} isLast={i === history.entries.length - 1} />)}
            {history.total > history.entries.length && (
              <button onClick={() => setHistoryLimit((l) => l + 10)} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 mt-4 ml-6" data-testid="history-load-more">
                Load more ({history.total - history.entries.length} remaining)
              </button>
            )}
          </div>
        )}
      </div>

      {showLogModal && <LogPaymentModal onClose={() => setShowLogModal(false)} onSaved={() => { setShowLogModal(false); load(); }} />}
    </Layout>
  );
}

function StatCard({ label, value, sub, tone = "default", testid }) {
  const toneClass = {
    default: "text-slate-900",
    green: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-600",
  }[tone];
  return (
    <div className="pm-card p-4" data-testid={testid}>
      <div className={`font-display font-black text-3xl font-mono-tab ${toneClass}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function CommissionScheduleSection({ testid }) {
  const [data, setData] = useState({ representation_windows: [], one_off_payments: [] });
  useEffect(() => {
    api.get("/commissions/schedule").then((r) => setData(r.data || { representation_windows: [], one_off_payments: [] }));
  }, []);
  const upcoming = (data.representation_windows || []).filter((w) => !w.is_past).slice(0, 3);
  const past = (data.representation_windows || []).filter((w) => w.is_past).slice(-2);
  const oneOffs = data.one_off_payments || [];

  if (upcoming.length === 0 && past.length === 0 && oneOffs.length === 0) return null;

  return (
    <div className="pm-card p-6 mb-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-bold tracking-tight">Commission schedule</h3>
          <div className="text-xs text-slate-500 mt-0.5">Biannual representation windows · one-off payments</div>
        </div>
      </div>

      {upcoming.length > 0 && (
        <div className="space-y-4 mb-5">
          {upcoming.map((w) => (
            <ScheduleWindow key={w.key} window={w} />
          ))}
        </div>
      )}

      {oneOffs.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
            One-off payments
          </div>
          <div className="divide-y divide-slate-100">
            {oneOffs.map((e) => (
              <div key={e.id} className="py-3 flex items-center justify-between" data-testid={`one-off-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 text-sm truncate">{e.client_name || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {e.description || e.commission_type}
                    {e.instalment_number ? ` · instalment ${e.instalment_number}/${e.total_instalments}` : ""}
                    {" · due "}
                    {e.due_date}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display font-black font-mono-tab text-slate-900">
                    €{(e.amount || 0).toLocaleString("en-GB")}
                  </div>
                  {e.status_computed === "overdue" && (
                    <span className="pm-pill pm-pill-red text-[10px] mt-1">Overdue {e.days_overdue}d</span>
                  )}
                  {e.status_computed === "due_soon" && (
                    <span className="pm-pill pm-pill-amber text-[10px] mt-1">In {e.days_until_due}d</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {past.length > 0 && (
        <details className="mt-4 pt-4 border-t border-slate-100">
          <summary className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 cursor-pointer">
            Past windows ({past.length})
          </summary>
          <div className="mt-3 space-y-3">
            {past.map((w) => (
              <ScheduleWindow key={w.key} window={w} muted />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function ScheduleWindow({ window: w, muted = false }) {
  return (
    <div
      className={`rounded-xl border ${muted ? "border-slate-100 bg-slate-50/60" : "border-slate-200"} p-4`}
      data-testid={`schedule-window-${w.key}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
            {muted ? "Past window" : "Upcoming window"}
          </div>
          <div className="font-display text-lg font-bold tracking-tight text-slate-900 mt-0.5">
            {w.label} — Representation
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-black font-mono-tab text-2xl text-emerald-700">
            €{(w.total || 0).toLocaleString("en-GB")}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
            {muted ? "(due in past)" : `due in ${w.days_until} days`}
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {(w.entries || []).map((e) => (
          <div key={e.id} className="py-2 flex items-center justify-between text-sm" data-testid={`window-entry-${e.id}`}>
            <div className="flex-1 min-w-0">
              <span className="font-bold text-slate-800">{e.client_name || "—"}</span>
              <span className="text-slate-500"> · {e.client_club || ""}</span>
            </div>
            <div className="font-mono-tab font-bold text-slate-800">
              €{(e.amount || 0).toLocaleString("en-GB")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReceiptRow({ entry, onMarkReceived }) {
  return (
    <div className="flex items-center gap-4 py-3" data-testid={`receipt-row-${entry.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm text-slate-900">{entry.client_name || "—"}</div>
        <div className="text-xs text-slate-500">{entry.description || PAYMENT_TYPE_LABEL[entry.payment_type] || "—"}</div>
      </div>
      <div className="font-mono-tab text-sm text-slate-700 hidden md:block">
        {fmtDate(entry.received_date || entry.due_date)}
      </div>
      <div className="font-mono-tab font-bold text-emerald-700 w-32 text-right">{fmtMoney(entry.amount, entry.currency)}</div>
      <span className={`pm-pill ${STATUS_TONE[entry.status]} w-24 text-center`}>{entry.status}</span>
      {entry.status === "pending" && (
        <button onClick={onMarkReceived} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid={`mark-received-${entry.id}`}>
          <CheckCircle2 size={12} /> Mark received
        </button>
      )}
    </div>
  );
}

function OutstandingRow({ entry, onMarkReceived, refCb, highlighted }) {
  const isOverdue = entry.status_computed === "overdue" || entry.days_overdue > 0;
  const isDueSoon = entry.status_computed === "due_soon";
  const isFifa = entry.payment_type === "solidarity";

  const chase = () => {
    const subject = encodeURIComponent(`Payment reminder — ${entry.description || entry.client_name}`);
    const body = encodeURIComponent(
      `Hi,\n\nThis is a friendly reminder regarding the outstanding payment for ${entry.client_name}: ${entry.description || "monthly commission"} (${fmtMoney(entry.amount, entry.currency)}) which was due on ${fmtDate(entry.due_date)}.\n\nCould you please confirm when this will be settled?\n\nBest regards,`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div
      ref={refCb}
      className={`flex items-center gap-4 py-3 rounded-lg px-2 transition-all ${highlighted ? "ring-2 ring-emerald-400 bg-emerald-50/60 animate-pulse" : ""}`}
      data-testid={`outstanding-row-${entry.id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isFifa && <span className="pm-pill pm-pill-blue text-[10px]">FIFA TMS</span>}
        <div className="min-w-0">
          <div className="font-display font-bold text-sm text-slate-900 truncate">{entry.description || PAYMENT_TYPE_LABEL[entry.payment_type] || "—"}</div>
          <div className="text-xs text-slate-500">{entry.client_name || "—"}</div>
        </div>
      </div>
      <div className="text-right hidden md:block">
        <div className={`font-mono-tab text-sm font-bold ${isOverdue ? "text-red-600" : isDueSoon ? "text-amber-700" : "text-slate-800"}`}>
          {fmtDate(entry.due_date)}
        </div>
        <div className="text-[11px] text-slate-500">
          {isOverdue ? `Overdue ${entry.days_overdue}d` : entry.days_until_due != null ? `Due in ${entry.days_until_due}d` : ""}
        </div>
      </div>
      <div className="font-mono-tab font-bold w-28 text-right">{fmtMoney(entry.amount, entry.currency)}</div>
      <div className="flex items-center gap-1">
        {isOverdue && (
          <button onClick={chase} className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1" data-testid={`chase-${entry.id}`}>
            <Send size={12} /> Chase
          </button>
        )}
        <button onClick={onMarkReceived} className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 ml-2" data-testid={`outstanding-mark-received-${entry.id}`}>
          <CheckCircle2 size={12} /> Mark received
        </button>
      </div>
    </div>
  );
}

function HistoryRow({ entry, isLast }) {
  return (
    <div className="flex gap-3" data-testid={`history-row-${entry.id}`}>
      <div className="flex flex-col items-center">
        <div className="w-3 h-3 rounded-full bg-emerald-500 mt-1.5" />
        {!isLast && <div className="flex-1 w-px bg-slate-200 mt-1" />}
      </div>
      <div className="flex-1 pb-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-sm text-slate-900">{fmtMoney(entry.amount, entry.currency)}</div>
            <div className="text-xs text-slate-500">{entry.client_name} · {entry.description || PAYMENT_TYPE_LABEL[entry.payment_type]}</div>
          </div>
          <div className="text-xs text-slate-400 font-mono-tab">{fmtDate(entry.received_date || entry.due_date)}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-center py-8 text-sm text-slate-500 italic">{text}</div>;
}

function LogPaymentModal({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: "",
    payment_type: "monthly_commission",
    amount: "",
    received_date: new Date().toISOString().slice(0, 10),
    reference: "",
    notes: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || []));
  }, []);

  const save = async () => {
    if (!form.client_id || !form.amount) return;
    setSaving(true);
    try {
      await api.post("/commissions/entries", {
        ...form,
        amount: parseFloat(form.amount),
        status: "received",
      });
      onSaved();
    } catch (e) {
      console.error(e);
      alert("Failed to log payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="log-payment-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display font-black text-lg tracking-tight">Log payment received</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <Field label="Player">
            <select className="pm-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="log-payment-client">
              <option value="">Select player…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </Field>
          <Field label="Payment type">
            <select className="pm-input" value={form.payment_type} onChange={(e) => setForm({ ...form, payment_type: e.target.value })} data-testid="log-payment-type">
              <option value="monthly_commission">Monthly commission</option>
              <option value="transfer_fee">Transfer fee</option>
              <option value="bonus">Bonus</option>
              <option value="solidarity">FIFA solidarity</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Amount (EUR)">
            <input type="number" step="0.01" className="pm-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="log-payment-amount" />
          </Field>
          <Field label="Date received">
            <input type="date" className="pm-input" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} data-testid="log-payment-date" />
          </Field>
          <Field label="Reference (optional)">
            <input className="pm-input" placeholder="Invoice number, FIFA TMS ref, etc." value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="log-payment-reference" />
          </Field>
          <Field label="Notes (optional)">
            <textarea className="pm-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="log-payment-notes" />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="pm-btn-secondary h-9 px-3 rounded-lg text-sm font-bold">Cancel</button>
          <button onClick={save} disabled={!form.client_id || !form.amount || saving} className="pm-btn-primary h-9 px-4 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="log-payment-save">
            {saving ? "Saving…" : "Log payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

/* ─────────────────────────────── Splits — Our share vs Owed to others ─────────────────────────────── */

function BreakdownSection({ testid }) {
  const [data, setData] = useState(null);
  const [editEntryId, setEditEntryId] = useState(null);

  const refresh = () => {
    api.get("/commissions/breakdown").then((r) => setData(r.data));
  };
  useEffect(() => { refresh(); }, []);

  if (!data) return null;
  if (data.our_share.length === 0 && data.owed_to_others.length === 0) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5" data-testid={testid}>
      <BreakdownCard
        title="Our share — what we receive"
        subtitle={data.agency_name}
        rows={data.our_share}
        total={data.our_share_total}
        tone="emerald"
        rowKey="our"
        testid="our-share-card"
        onEdit={setEditEntryId}
      />
      <BreakdownCard
        title="Owed to others"
        subtitle="Partner agents · family · sub-agents"
        rows={data.owed_to_others}
        total={data.owed_to_others_total}
        tone="amber"
        rowKey="owed"
        testid="owed-card"
        onEdit={(eid) => setEditEntryId(eid)}
      />
      {editEntryId && (
        <SplitsEditorModal
          entryId={editEntryId}
          onClose={() => setEditEntryId(null)}
          onSaved={() => { setEditEntryId(null); refresh(); }}
        />
      )}
    </div>
  );
}

function BreakdownCard({ title, subtitle, rows, total, tone, rowKey, testid, onEdit }) {
  const totalCls = tone === "emerald" ? "text-emerald-700" : "text-amber-700";
  return (
    <div className="pm-card p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
          <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
        </div>
        <div className={`font-display font-black font-mono-tab text-2xl ${totalCls}`}>
          €{(total || 0).toLocaleString("en-GB")}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-6 text-center">Nothing outstanding here yet.</div>
      ) : (
        <div className="divide-y divide-slate-100 mt-3 max-h-[420px] overflow-y-auto">
          {rows.slice(0, 30).map((r, i) => (
            <div
              key={r.split_id || `${rowKey}-${r.commission_id}-${i}`}
              className="py-2.5 flex items-center justify-between text-sm hover:bg-slate-50 -mx-2 px-2 rounded cursor-pointer"
              onClick={() => onEdit(r.commission_id)}
              data-testid={`breakdown-row-${rowKey}-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate">
                  {r.client_name || "—"}{" "}
                  {r.recipient_name && (
                    <span className="font-normal text-slate-500 text-xs">→ {r.recipient_name}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {r.commission_type} · {r.pct}% · due {r.due_date}
                  {r.days_overdue > 0 && <span className="text-red-600 ml-1 font-bold">· {r.days_overdue}d overdue</span>}
                  {r.mandate_id && <span className="ml-1 text-emerald-700">· mandate ✓</span>}
                </div>
              </div>
              <div className="font-mono-tab font-bold text-slate-800 shrink-0 pl-3">
                €{(r.amount || 0).toLocaleString("en-GB")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RECIPIENT_TYPES = [
  { v: "agency", label: "Agency (internal)" },
  { v: "partner_agent", label: "Partner agent" },
  { v: "family", label: "Family member" },
  { v: "sub_agent", label: "Sub-agent" },
  { v: "other", label: "Other" },
];

function SplitsEditorModal({ entryId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/commissions/entries/${entryId}/splits`).then((r) => {
      setData(r.data);
      const initial = r.data.splits.map((s) => ({
        recipient_type: s.recipient_type,
        recipient_name: s.recipient_name,
        split_pct: s.split_pct,
        payment_status: s.payment_status,
        mandate_id: s.mandate_id || null,
      }));
      setRows(initial.length > 0 ? initial : [
        { recipient_type: "agency", recipient_name: "Our agency", split_pct: 100, payment_status: "pending", mandate_id: null },
      ]);
    });
  }, [entryId]);

  if (!data) return null;
  const amount = data.entry_amount;
  const totalPct = rows.reduce((s, r) => s + (Number(r.split_pct) || 0), 0);

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, j) => j === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRows((rs) => rs.filter((_, j) => j !== i));
  const addRow = () => setRows((rs) => [...rs, { recipient_type: "partner_agent", recipient_name: "", split_pct: 0, payment_status: "pending", mandate_id: null }]);

  const onSave = async () => {
    setError(null);
    if (Math.abs(totalPct - 100) > 0.05) {
      setError(`Splits total ${totalPct.toFixed(2)}% — must equal 100% before saving.`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/commissions/entries/${entryId}/splits`, { splits: rows });
      onSaved && onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="splits-editor-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-white">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-600">Commission split</div>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1">
            €{amount.toLocaleString("en-GB")} {data.entry_currency}
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">How is this commission divided?</div>
        </div>

        <div className="px-6 py-5 space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`split-row-${i}`}>
              <select
                value={r.recipient_type}
                onChange={(e) => updateRow(i, { recipient_type: e.target.value })}
                className="col-span-3 h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white"
                data-testid={`split-type-${i}`}
              >
                {RECIPIENT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
              <input
                type="text"
                value={r.recipient_name}
                onChange={(e) => updateRow(i, { recipient_name: e.target.value })}
                placeholder="Recipient name"
                className="col-span-5 h-10 rounded-lg border border-slate-200 px-3 text-sm"
                data-testid={`split-name-${i}`}
              />
              <div className="col-span-2 relative">
                <input
                  type="number"
                  value={r.split_pct}
                  step="0.5"
                  min="0"
                  max="100"
                  onChange={(e) => updateRow(i, { split_pct: Number(e.target.value) })}
                  className="w-full h-10 rounded-lg border border-slate-200 px-2 text-sm font-mono-tab text-right pr-7"
                  data-testid={`split-pct-${i}`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
              </div>
              <div className="col-span-1 text-right font-mono-tab text-xs text-slate-600">
                €{Math.round(amount * (Number(r.split_pct) || 0) / 100).toLocaleString("en-GB")}
              </div>
              <button onClick={() => removeRow(i)} className="col-span-1 text-slate-400 hover:text-red-600" data-testid={`split-remove-${i}`} title="Remove">
                ✕
              </button>
            </div>
          ))}

          <button
            onClick={addRow}
            className="text-emerald-700 hover:text-emerald-900 text-sm font-bold flex items-center gap-1 mt-2"
            data-testid="split-add-btn"
          >
            <Plus size={14} /> Add recipient
          </button>

          <div className={`mt-4 p-3 rounded-lg text-sm flex items-center justify-between ${Math.abs(totalPct - 100) > 0.05 ? "bg-red-50 text-red-800 border border-red-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`} data-testid="splits-total">
            <span className="font-bold">Total</span>
            <div className="flex items-center gap-3">
              <span className="font-mono-tab font-bold">{totalPct.toFixed(2)}%</span>
              <span className="text-xs">{Math.abs(totalPct - 100) > 0.05 ? "Must equal 100%" : "✓"}</span>
            </div>
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="splits-error">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-sm font-bold">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving || Math.abs(totalPct - 100) > 0.05}
            className="pm-btn-primary h-10 px-5 rounded-lg text-sm font-bold disabled:opacity-50"
            data-testid="splits-save-btn"
          >
            {saving ? "Saving…" : "Save split"}
          </button>
        </div>
      </div>
    </div>
  );
}

